/*
 * FILE LOCATION: frontend/src/hooks/useAuth.js
 * REPLACE: Yes, replace the entire existing file
 */

import { useState, useEffect, useCallback } from "react";
import { authService } from "../services/auth";
import { tokenStorage } from "../services/api";

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch current user from API if token exists
   */
  const fetchUser = useCallback(async () => {
    const token = tokenStorage.get();
    if (!token) {
      setLoading(false);
      setUser(null);
      return;
    }

    try {
      const me = await authService.getCurrentUser();
      setUser(me);
    } catch (err) {
      console.error("Auth check failed", err);
      tokenStorage.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check authentication on mount
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  /**
   * Login user with email and password
   */
  const login = async (email, password, remember = true) => {
    setLoading(true);
    setError(null);

    try {
      const { user: loggedInUser } = await authService.login(email, password, remember);
      setUser(loggedInUser);
      console.log('✅ Login successful, user set:', loggedInUser);
      return loggedInUser;
    } catch (err) {
      console.error("Login failed", err);
      setError(err.response?.data?.detail || "Login failed");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register new user
   */
  const register = async (formData) => {
    setLoading(true);
    setError(null);

    try {
      console.log('📤 Starting registration process...');

      const { user: registeredUser } = await authService.register(formData);

      setUser(registeredUser);
      console.log('✅ Registration complete, user set:', registeredUser);

      return registeredUser;
    } catch (err) {
      console.error("❌ Registration failed", err);
      setError(err.response?.data?.detail || "Registration failed");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout user and clear session
   */
  const logout = () => {
    authService.logout();
    setUser(null);
  };

  /**
   * Refresh user data from API
   */
  const refreshUser = async () => {
    try {
      const me = await authService.getCurrentUser();
      setUser(me);
      return me;
    } catch (err) {
      console.error("Failed to refresh user", err);
      throw err;
    }
  };

  return {
    user,
    loading,
    error,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
  };
}